import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import { useWebSocket } from '../contexts/WebSocketContext'
import {
  MessageSquare,
  Send,
  Clock,
  AlertCircle,
  CheckCircle,
  Plus,
  X
} from 'lucide-react'

interface SupportTicket {
  id: number
  session_id: string
  status: 'waiting' | 'active' | 'closed'
  reason: string
  assigned_to_name?: string
  started_at: string
  closed_at?: string
  unread_count: number
}

interface TicketMessage {
  id: number
  message: string
  sender_type: 'visitor' | 'agent' | 'system'
  agent_name?: string
  is_internal: boolean
  created_at: string
  read_at?: string
}

const BrokerSupport: React.FC = () => {
  const { t } = useTranslation()
  const { socket } = useWebSocket()
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null)
  const [messages, setMessages] = useState<TicketMessage[]>([])
  const [messageInput, setMessageInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [showNewTicketModal, setShowNewTicketModal] = useState(false)
  const [newTicketSubject, setNewTicketSubject] = useState('')
  const [newTicketMessage, setNewTicketMessage] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    fetchTickets()
  }, [])

  useEffect(() => {
    if (!socket) return

    // Listen for new messages
    socket.on('chat:message', (data: any) => {
      if (selectedTicket && data.session_id === selectedTicket.session_id) {
        setMessages(prev => [...prev, data])
      }
      fetchTickets()
    })

    // Listen for status changes
    socket.on('chat:status_change', (data: any) => {
      if (selectedTicket && data.session_id === selectedTicket.session_id) {
        setSelectedTicket(prev => prev ? { ...prev, status: data.status } : null)
      }
      fetchTickets()
    })

    return () => {
      socket.off('chat:message')
      socket.off('chat:status_change')
    }
  }, [socket, selectedTicket])

  const fetchTickets = async () => {
    try {
      const response = await makeApiCall('/api/chat/tickets/my-tickets')
      const data = await response.json()
      console.log('Fetched tickets:', data.tickets) // Debug log
      setTickets(data.tickets || [])
    } catch (error) {
      console.error('Error fetching support tickets:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadTicket = async (ticket: SupportTicket) => {
    try {
      const response = await makeApiCall(`/api/chat/tickets/${ticket.session_id}`)
      const data = await response.json()
      setSelectedTicket(data.ticket)
      setMessages(data.messages || [])

      // Mark as read
      await makeJsonApiCall(`/api/chat/tickets/${ticket.session_id}/mark-read`, 'PUT', {})
      fetchTickets()
    } catch (error) {
      console.error('Error loading ticket:', error)
      alert('Failed to load support ticket')
    }
  }

  const sendMessage = async () => {
    if (!selectedTicket || !messageInput.trim()) return

    try {
      setSending(true)
      await makeJsonApiCall(
        `/api/chat/tickets/${selectedTicket.session_id}/reply`,
        'POST',
        { message: messageInput }
      )
      setMessageInput('')
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const createTicket = async () => {
    if (!newTicketSubject.trim() || !newTicketMessage.trim()) return

    try {
      setCreating(true)
      const response = await makeJsonApiCall(
        '/api/chat/tickets/create',
        'POST',
        {
          subject: newTicketSubject,
          message: newTicketMessage,
          priority: 'normal'
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Ticket creation failed:', errorData)
        throw new Error(errorData.error || 'Failed to create ticket')
      }

      const data = await response.json()
      console.log('Ticket created:', data) // Debug log
      
      setShowNewTicketModal(false)
      setNewTicketSubject('')
      setNewTicketMessage('')
      
      // Refresh tickets list with a small delay to ensure backend has committed
      setTimeout(async () => {
        await fetchTickets()
      }, 500)
      
      alert(data.message || 'Support request submitted successfully')
    } catch (error) {
      console.error('Error creating ticket:', error)
      alert(error instanceof Error ? error.message : 'Failed to create support request')
    } finally {
      setCreating(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'waiting': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-900">
      {/* Sidebar - Support Tickets */}
      <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-blue-600" />
              Support Tickets
            </h1>
            <button
              onClick={() => setShowNewTicketModal(true)}
              className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              title="Create new support request"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {tickets.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No support tickets</p>
              <button
                onClick={() => setShowNewTicketModal(true)}
                className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Create your first ticket
              </button>
            </div>
          ) : (
            tickets.map(ticket => (
              <button
                key={ticket.id}
                onClick={() => loadTicket(ticket)}
                className={`w-full p-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left ${
                  selectedTicket?.id === ticket.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="font-medium text-gray-900 dark:text-white text-sm">
                    {ticket.reason}
                  </div>
                  {ticket.unread_count > 0 && (
                    <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                      {ticket.unread_count}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(ticket.started_at).toLocaleDateString()}
                  </span>
                </div>
                {ticket.assigned_to_name && (
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Assigned to: {ticket.assigned_to_name}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {selectedTicket ? (
          <>
            <div className="p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedTicket.reason}
                  </h2>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatTime(selectedTicket.started_at)}
                    </span>
                    {selectedTicket.assigned_to_name && (
                      <span>Assigned to: {selectedTicket.assigned_to_name}</span>
                    )}
                  </div>
                </div>
                <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(selectedTicket.status)}`}>
                  {selectedTicket.status}
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`flex ${
                    message.sender_type === 'agent' ? 'justify-start' : 'justify-end'
                  }`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-3 ${
                      message.sender_type === 'agent'
                        ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                        : message.sender_type === 'system'
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-center italic'
                        : 'bg-blue-600 text-white'
                    }`}
                  >
                    {message.sender_type === 'agent' && message.agent_name && (
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {message.agent_name}
                      </div>
                    )}
                    <div className={message.sender_type === 'agent' ? 'text-gray-900 dark:text-white' : ''}>
                      {message.message}
                    </div>
                    <div className={`text-xs mt-1 ${
                      message.sender_type === 'agent'
                        ? 'text-gray-500 dark:text-gray-400'
                        : 'text-blue-100'
                    }`}>
                      {new Date(message.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {selectedTicket.status !== 'closed' && (
              <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={sending}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!messageInput.trim() || sending}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg font-medium">Select a ticket to view</p>
              <p className="text-sm mt-2">Choose a support ticket from the sidebar</p>
            </div>
          </div>
        )}
      </div>

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Create Support Request
              </h2>
              <button
                onClick={() => setShowNewTicketModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={newTicketSubject}
                  onChange={(e) => setNewTicketSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Message
                </label>
                <textarea
                  value={newTicketMessage}
                  onChange={(e) => setNewTicketMessage(e.target.value)}
                  placeholder="Provide details about your issue or question..."
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowNewTicketModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={createTicket}
                  disabled={!newTicketSubject.trim() || !newTicketMessage.trim() || creating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating...' : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BrokerSupport
